import React from 'react';
import { Map, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';

export const Sitemap: React.FC = () => {
  const sitemapSections: { title: string; links: { name: string; path: string; external?: boolean }[] }[] = [
    {
      title: 'Main Pages',
      links: [
        { name: 'Home', path: '/' },
        { name: 'About', path: '/about' },
        { name: 'Contact', path: '/contact' },
        { name: 'Consultation', path: '/consultation' },
      ]
    },
    {
      title: 'Services & Products',
      links: [
        { name: 'Free Tools', path: '/free-tools' },
        { name: 'Store', path: '/store' },
        { name: 'All Projects', path: '/portfolio' },
      ]
    },
    {
      title: 'Portfolio Projects',
      links: [
        { name: 'Local Route', path: '/portfolio/local-route' },
        { name: 'Phonetic Pro', path: '/portfolio/phonetic' },
      ]
    },
    {
      title: 'Legal',
      links: [
        { name: 'Privacy Policy', path: '/privacy' },
        { name: 'Terms of Service', path: '/terms' },
        { name: 'Cookies Policy', path: '/cookies' },
        { name: 'Disclaimer', path: '/disclaimer' },
      ]
    },
    {
      title: 'External Links',
      links: [
        { name: 'Blog', path: 'https://blog.cemates.me', external: true },
        { name: 'GitHub', path: 'https://github.com/cemates', external: true },
        { name: 'LinkedIn', path: 'https://www.linkedin.com/in/cematesh/', external: true },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Sitemap"
        description="Navigate all pages on Cemates.me. Find links to services, portfolio, legal pages, and more."
        canonical="/sitemap"
      />
      <div className="pt-32 pb-20">
        <div className="max-w-2xl mx-auto px-6">
          {/* Header */}
          <div className="mb-16">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-muted mb-6">
              <Map className="w-5 h-5 text-foreground" />
            </div>
            <h1 className="text-3xl font-semibold text-foreground mb-3">
              Sitemap
            </h1>
            <p className="text-muted-foreground text-sm">
              Navigate through all pages on cemates.me
            </p>
          </div>

          {/* Intro */}
          <div className="mb-12">
            <p className="text-muted-foreground leading-relaxed">
              Find all the pages and resources available on our website. 
              Use the links below to navigate to any section quickly.
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-12">
            {sitemapSections.map((section, index) => (
              <div key={index}>
                <h2 className="text-lg font-medium text-foreground mb-4">
                  {section.title}
                </h2>
                <ul className="space-y-3">
                  {section.links.map((link, linkIndex) => (
                    <li key={linkIndex}>
                      {link.external ? (
                        <a 
                          href={link.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground text-sm leading-relaxed flex items-center gap-2 hover:text-foreground transition-colors group"
                        >
                          <ArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                          {link.name}
                          <span className="text-xs text-muted-foreground/50">↗</span>
                        </a>
                      ) : (
                        <Link 
                          to={link.path}
                          className="text-muted-foreground text-sm leading-relaxed flex items-center gap-2 hover:text-foreground transition-colors group"
                        >
                          <ArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                          {link.name}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
